import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import { SelectSharedProps } from '@mantine/core/lib/components/Select/Select';
import { Button, Group, NumberInput, Select, Space, Text, Textarea, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { endpoints } from '../api';
import { SupportedLanguage, resolveLanguage } from '../i18n';
import { ConfiguredForm } from './formConfiguration';
import { useMediaQuery } from '@mantine/hooks';


type SpecialtiesData = SelectSharedProps<{ id: number, value: string, label: string }, string>['data']

type PersonalInformationProps = {
  setStep: any;
  form: ConfiguredForm;
};

export const PersonalInformation: React.FC<PersonalInformationProps> = ({ setStep, form }) => {
  const { t, i18n } = useTranslation()
  const isDesktop = useMediaQuery('(min-width: 576px)');
  const handleSubmit = () => {
    const validationResults = form.validate();
    const fieldsWeCareAbout = ['specialisation', 'experience', 'education', 'about'];
    let canContinue = true;
    fieldsWeCareAbout.forEach(key => {
      if (validationResults.errors[key]) {
        canContinue = false;
      }
    });

    if (!canContinue) return;
    // Go to next step
    form.clearErrors();
    setStep((prev: any) => prev + 1);
  };

  const [specialtiesByLang, setSpecialtiesByLang] = useState<Record<SupportedLanguage, SpecialtiesData | null>>({ru: null, en: null})

  const language = resolveLanguage(i18n)

  const specialties = useMemo<SpecialtiesData>(
    () => specialtiesByLang[language] ?? [],
    [specialtiesByLang, language])

  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const lang = language
    if (specialtiesByLang[lang] || isLoading) return
    setIsLoading(true)
    endpoints.getSpecialties(lang)
      .then((res) => {
        setSpecialtiesByLang((state) => ({
          ...state,
          [lang]: res.data.list.map((x) => ({
            id: x.id,
            value: x.key,
            label: x.name
          }))
        }))
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [specialtiesByLang, language, isLoading])

  return (
    <>
      <Title order={3}>{t('about.subtitle')}</Title>
      <Space h={'md'} />
      <Select
        disabled={isLoading}
        required
        label={t('about.inputs.specialties.label')}
        placeholder={t('about.inputs.specialties.placeholder')}
        data={specialties}
        searchable
        description={t('about.inputs.specialties.description')}
        nothingFound={t('about.inputs.specialties.nothingFound')}
        {...form.getInputProps('specialisation')}
      />
      <Space h={'md'} />
      <NumberInput
        hideControls
        required
        label={t('about.inputs.experience.label')}
        placeholder={t('about.inputs.experience.placeholder')}
        description={t('about.inputs.experience.description')}
        max={100}
        min={1}
        rightSection={
          <div style={{ marginRight: 16 }}>
            <Text color="gray" size="sm">
              {t('about.inputs.experience.additionalLabel')}
            </Text>
          </div>
        }
        {...form.getInputProps('experience')}
      />
      <Space h={'md'} />
      <Textarea
        required
        autosize
        minRows={2}
        label={t('about.inputs.education.label')}
        placeholder={t('about.inputs.education.placeholder')}
        description={t('about.inputs.education.description')}
        {...form.getInputProps('education')}
      />
      <Space h={'md'} />
      <Textarea
        autosize
        minRows={3}
        label={t('about.inputs.description.label')}
        placeholder={t('about.inputs.description.placeholder')}
        description={t('about.inputs.description.description')}
        {...form.getInputProps('about')}
      />
      <Group my="xl" position="right">
        <Button size="md" fullWidth={!isDesktop} color="blue" onClick={handleSubmit}>
          {t('common.buttons.next')}
        </Button>
      </Group>
    </>
  );
};
